import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle, XCircle, Shield, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { api, ApiError } from '@/api/client'

type PageState = 'ready' | 'loading' | 'success' | 'error'

export function DeviceVerify() {
  const [searchParams] = useSearchParams()
  const userCode = searchParams.get('user_code')
  const [state, setState] = useState<PageState>('ready')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleApprove = useCallback(async () => {
    if (!userCode) return

    setState('loading')
    setErrorMessage(null)

    try {
      // CF Access JWT is sent automatically via httpOnly cookie
      await api.post('/auth/device/approve', {
        user_code: userCode,
      })
      setState('success')
    } catch (err) {
      setState('error')
      if (err instanceof ApiError) {
        setErrorMessage(err.message)
      } else {
        setErrorMessage('An unexpected error occurred')
      }
    }
  }, [userCode])

  // No user code provided
  if (!userCode) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle>Invalid Request</CardTitle>
            <CardDescription>No user code provided in the URL.</CardDescription>
          </CardHeader>
          <CardContent className="text-center text-sm text-muted-foreground">
            Please use the complete authorization link from your CLI.
          </CardContent>
        </Card>
      </div>
    )
  }

  // Success state
  if (state === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
            <CardTitle>Device Authorized</CardTitle>
            <CardDescription>You can close this window and return to your terminal.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  // Ready/Loading/Error state
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <CardTitle>Authorize Device</CardTitle>
          <CardDescription>Confirm that you want to authorize this device to access your account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border bg-muted/50 p-4 text-center">
            <p className="mb-2 text-sm text-muted-foreground">User Code</p>
            <p className="font-mono text-2xl font-bold tracking-wider">{userCode}</p>
          </div>

          {state === 'error' && errorMessage && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {errorMessage}
            </div>
          )}

          <Button
            className="w-full"
            size="lg"
            onClick={handleApprove}
            disabled={state === 'loading'}
          >
            {state === 'loading' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Authorizing...
              </>
            ) : (
              'Approve'
            )}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            Only approve if you initiated this login from your terminal.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
